import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, Trash2, UserPlus } from 'lucide-react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../../__generated__/schema-lite';
import { useCurrentAccount, type AccountRole } from '../../account/AccountContext';
import { useCan } from '../../account/capabilities';
import { RoleBadge } from '../../components/RoleBadge';
import { translateError } from '../../lib/translate-error';

/** Who else hears this line's calls, and — for its owner — who may.
 *
 *  Everyone in the account reads the list. AccountMember's read rule admits every
 *  member deliberately: a delegate should know who else is listening. Only the owner
 *  gets the controls, which is `manageTeam`. The server refuses the rest either way —
 *  this decides what to render, not what is allowed.
 *
 *  OWNER rows carry no controls, even for the owner looking at their own. Both
 *  `setMemberRole` and `removeMember` refuse a peer owner and refuse you acting on
 *  yourself, because an account with no owner has nobody left who can repair it — so
 *  rendering those buttons could only produce a 403.
 *
 *  OWNER is likewise absent from the level dropdown. Promoting someone to owner is a
 *  transfer of the whole line and wants a deliberate flow with the new owner's consent,
 *  not a select; `inviteDelegate` and `setMemberRole` both refuse it. */
const DELEGATE_ROLES: readonly AccountRole[] = ['ADMIN', 'RESPONDER', 'VIEWER'];

type MemberRow = {
  id: string;
  userId: string;
  role: AccountRole;
  user?: { name?: string | null; email?: string } | null;
};

type InviteRow = { id: string; email: string; accountRole: AccountRole | null };

export default function TeamPage() {
  const { t } = useTranslation();
  const { accountId } = useCurrentAccount();
  const can = useCan();
  const manage = can('manageTeam');
  const crud = useClientQueries(schema);
  const queryClient = useQueryClient();

  const membersQ = crud.accountMember.useFindMany({
    where: { accountId },
    include: { user: { select: { name: true, email: true } } },
    orderBy: { createdAt: 'asc' },
  });

  // Only the owner may read invites, so asking as anyone else is a guaranteed empty
  // list and a wasted request.
  const invitesQ = crud.invite.useFindMany(
    { where: { accountId, usedAt: null }, orderBy: { createdAt: 'desc' } },
    { enabled: manage },
  );

  const inviteM = crud.$procs.inviteDelegate.useMutation();
  const setRoleM = crud.$procs.setMemberRole.useMutation();
  const removeM = crud.$procs.removeMember.useMutation();
  const revokeM = crud.invite.useDelete();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [newRole, setNewRole] = useState<AccountRole>('VIEWER');

  const members = (membersQ.data as MemberRow[] | undefined) ?? [];
  const invites = (invitesQ.data as InviteRow[] | undefined) ?? [];

  /** Procedure mutations cannot tell the cache which queries they affected — the
   *  library only infers that for model mutations — so the refetch is explicit. */
  const refresh = () => queryClient.invalidateQueries();

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    try {
      await inviteM.mutateAsync({
        args: { input: { accountId, email, role: newRole, name: name || undefined } },
      });
      // Clear only on success, so a rejected address stays in the field to be fixed.
      setEmail('');
      setName('');
      await refresh();
      toast.success(t('team.invited'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function onChangeRole(userId: string, role: AccountRole) {
    try {
      await setRoleM.mutateAsync({ args: { input: { accountId, userId, role } } });
      await refresh();
      toast.success(t('team.roleChanged'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function onRemove(userId: string, who: string) {
    if (!window.confirm(t('team.confirmRemove', { who }))) return;
    try {
      await removeM.mutateAsync({ args: { input: { accountId, userId } } });
      await refresh();
      toast.success(t('team.removed'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  async function onRevoke(id: string) {
    try {
      await revokeM.mutateAsync({ where: { id } });
      toast.success(t('team.revoked'));
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  return (
    <div className="h-full overflow-y-auto px-4 pt-6 pb-24">
      <div className="mx-auto max-w-md space-y-6">
        <h1 className="text-xl font-semibold">{t('nav.team')}</h1>
        {!manage ? <p className="text-sm text-muted-foreground">{t('team.readOnly')}</p> : null}

        <section className="space-y-2">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('team.members')}
          </h2>
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {members.map((m) => {
              // A VIEWER gets `email` back as null (field rule in schema.zmodel), so
              // someone who never set a name would otherwise show as a raw cuid. The
              // level and the fact that they are here is what a viewer needs anyway.
              const who = m.user?.name || m.user?.email || t('team.unnamed');
              return (
                <li key={m.id} className="space-y-2 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{who}</span>
                      {m.user?.name && m.user?.email ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {m.user.email}
                        </span>
                      ) : null}
                    </span>
                    <RoleBadge role={m.role} />
                  </div>

                  {manage && m.role !== 'OWNER' ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={m.role}
                        onChange={(e) => onChangeRole(m.userId, e.target.value as AccountRole)}
                        disabled={setRoleM.isPending}
                        className="input flex-1 py-1.5 text-sm"
                        aria-label={t('team.role')}
                      >
                        {DELEGATE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t(`accountRole.${r}`)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => onRemove(m.userId, who)}
                        disabled={removeM.isPending}
                        className="shrink-0 rounded-md border border-border p-2 text-muted-foreground hover:bg-muted hover:text-destructive"
                        aria-label={t('team.remove')}
                        title={t('team.remove')}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>

        {manage ? (
          <>
            <section className="space-y-2">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('team.pending')}
              </h2>
              {invites.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('team.noPending')}</p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                  {invites.map((i) => (
                    <li key={i.id} className="flex items-center gap-2 px-4 py-3">
                      <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-sm">{i.email}</span>
                      {i.accountRole ? <RoleBadge role={i.accountRole} /> : null}
                      <button
                        type="button"
                        onClick={() => onRevoke(i.id)}
                        disabled={revokeM.isPending}
                        className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                      >
                        {t('team.revoke')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <form
              onSubmit={onInvite}
              className="space-y-3 rounded-lg border border-border bg-card p-4"
            >
              <h2 className="text-sm font-medium">{t('team.inviteHeading')}</h2>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('field.email')}
                className="input w-full"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('field.nameOptional')}
                className="input w-full"
              />
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as AccountRole)}
                className="input w-full"
                aria-label={t('team.role')}
              >
                {DELEGATE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`accountRole.${r}`)}
                  </option>
                ))}
              </select>
              <button type="submit" disabled={inviteM.isPending} className="btn-primary w-full">
                <UserPlus className="mr-2 inline h-4 w-4" aria-hidden />
                {inviteM.isPending ? t('team.sending') : t('team.send')}
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}
